import React, { useState } from 'react';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { Button, Input } from 'antd';

import Drawer from 'components/Drawer';
import Table from 'components/Table';

import globalMessages from 'translations/messages';
import Order from 'components/Order';
import useFormatMessage from 'hooks/useFormatMessage';
import StatusTag from 'components/StatusTag';
import { PaymentStatus, PaymentType } from 'shared/prisma/types';
import CurrencyFormat from 'components/CurrencyFormat';
import PayOrderButton from 'components/PayOrderButton';
import { fullOrderInfo } from 'shared/utils/order';
import { dayjs } from 'shared/utils/date';

const QUERY = gql`
  query ($id: ID!) {
    customer (id: $id) {
      id      
      paymentType      
      orders {
        ${fullOrderInfo}        
        stripeInvoiceUrl
      }
    }
  }
`;

const columns = [
  {
    dataIndex: 'createdAt',
    title: 'Created',
    type: 'datetime',
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'orderNumber',
    title: 'Order',
    type: 'number',
    render: (orderNumber) => (orderNumber ? `#${orderNumber}` : null),
  },
  {
    dataIndex: 'dataSource',
    title: 'Data source',
  },
  {
    dataIndex: 'amount',
    title: 'Amount',
    type: 'number',
    render: (amount, record) => amount && <CurrencyFormat currency={record.currency} value={amount} />,
  },
  {
    dataIndex: 'paymentStatus',
    title: 'Payment status',
    align: 'center',
    render: (status, record) => record.amount > 0 && <StatusTag status={status} />,
  },
  {
    dataIndex: 'paymentDate',
    title: 'Payment date',
    type: 'datetime',
    render: (paymentDate, record) => {
      if (paymentDate) {
        return dayjs(paymentDate).format('YYYY-MM-DD');
      }
      if (record.amount > 0 && record.paymentStatus === PaymentStatus.Pending && record.startDate && dayjs(record.startDate).isAfter(dayjs(), 'day')) {
        return `Scheduled: ${dayjs(record.startDate).format('YYYY-MM-DD')}`;
      }
      return null;
    },
  },
];

const CustomerOrders = ({ customer }) => {
  const $t = useFormatMessage();
  const [search, setSearch] = useState();
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const { data, loading } = useQuery(QUERY, {
    variables: {
      id: customer.id,
    },
  });

  const actions = (record) => (
    <>
      {[PaymentStatus.Pending, PaymentStatus.Failed].includes(record.paymentStatus) && record.amount > 0 && data?.customer.paymentType !== PaymentType.Invoice && (
        <PayOrderButton orderId={record.id} customerId={customer.id} />
      )}
      <Button onClick={() => setShowOrderDetails(record)}>
        Details
      </Button>
    </>
  );

  return (
    <>
      <Input.Search
        onSearch={setSearch}
        allowClear
        placeholder={$t(globalMessages.search)}
        style={{ marginBottom: '1em' }}
      />
      <Table
        loading={loading}
        columns={columns}
        dataSource={data?.customer.orders}
        actions={actions}
        search={search}
      />
      <Drawer
        title="Order details"
        open={!!showOrderDetails}
        onClose={() => setShowOrderDetails(false)}
        footer={false}
      >
        {showOrderDetails && <Order order={showOrderDetails} />}
        {showOrderDetails.stripeInvoiceUrl && (
          <Button type="primary" href={showOrderDetails.stripeInvoiceUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: '1em' }}>
            View invoice
          </Button>
        )}
      </Drawer>
    </>
  );
};

export default CustomerOrders;
